import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  IProjectFilters,
  ISimpleProject,
  ProjectDto,
  PROJECT_STATUS_KEY,
  ProjectBid,
  ProjectStatusDto,
} from '../pages/webapp/projects/projects.interface';
import {
  BUDGET_TEMPLATES_GC,
  BUDGET_TEMPLATES_MANAGER,
  REST_LINE_ITEMS_SIMPLE,
  PROJECTS_SIMPLE,
  REST_PROJECT_BID,
  REST_PROJECT_STATS,
  REST_PROJECT_STATUS,
  REST_PROJECT_STATUS_TYPES,
  REST_PROJECTS,
  REST_PROPERTY,
  REST_PROPERTY_TYPES,
  FORECAST_MODIFICATIONS,
  REST_GET_INVITED_SUBCONTRACTORS_FOR_PROJECT,
} from '../restApi/RestRoutes';
import { ErrorHandlerRest } from '../restApi/errorHandler-rest';
import { RestRequestService } from '../restApi/rest-request.service';
import { IProperty } from '../store/properties/properties.interfaces';
import {
  IRollupLineItems,
  IRollupPrimeProject,
  IRollupProject,
  IRollupSubProject,
  IRollupTag,
  ROLLUP_VIEW_TYPES,
} from '../store/rollups/rollups.interface';
import { InvitedSubcontractor } from '../framework/constants/user.constants';
import { specialCharReplaceRegexp } from '../framework/constants/spend.constants';
import {
  IBudgetTemplate,
  IBudgetTemplateItemGC,
  IBudgetTemplateItemManager,
} from '../store/templates/templates.types';
import { CurrentUserService } from './current-user.service';
import { ILineItemSimple } from '@app/store/spend/spend.interfaces';
import { IForecastModificationBudgetLine } from '@app/framework/interaction-bar/forecast-modifications-sidebar/forecast-modifications-sidebar.type';
import { AnticipatedIncome } from '@app/framework/interfaces/anticipated-income.type';

type RollupResponseMap = {
  [ROLLUP_VIEW_TYPES.PROJECTS]: IRollupProject[];
  [ROLLUP_VIEW_TYPES.SP_PROJECTS]: IRollupSubProject[];
  [ROLLUP_VIEW_TYPES.SUB_PROJECT]: IRollupSubProject[];
  [ROLLUP_VIEW_TYPES.PRIME_PROJECT]: IRollupPrimeProject[];
  [ROLLUP_VIEW_TYPES.BUDGET_LINE_ITEMS]: IRollupLineItems[];
  [ROLLUP_VIEW_TYPES.TAGS]: IRollupTag[];
};

@Injectable({
  providedIn: 'root',
})
export class ProjectApiService extends ErrorHandlerRest {
  constructor(
    private rest: RestRequestService,
    protected user: CurrentUserService,
    protected router: Router,
  ) {
    super(user, router);
  }

  getRollup<T extends ROLLUP_VIEW_TYPES>(
    view: T,
    search = '',
    propertyIds: number[] = [],
    tags: string[] = [],
    projectStatusIds: number[] = [],
    projectIds: number[] = [],
    showZeroDollarLines = true,
  ) {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('view', view);
    queryParams = queryParams.append('search', search);
    queryParams = queryParams.append('showZeroDollarLines', showZeroDollarLines ? '1' : '0');
    propertyIds.forEach((propertyId, index) => {
      queryParams = queryParams.append(`propertyIds[${index}]`, propertyId);
    });
    projectStatusIds.forEach((projectStatusId, index) => {
      queryParams = queryParams.append(`projectStatusIds[${index}]`, projectStatusId);
    });
    projectIds.forEach((projectId, index) => {
      queryParams = queryParams.append(`projectIds[${index}]`, projectId);
    });
    queryParams = this.getTagsQueryParams(queryParams, tags);

    return this.rest.getWithObservable<RollupResponseMap[T]>(
      REST_PROJECTS + `/rollup?${queryParams.toString()}`,
    );
  }

  getTagsQueryParams = (queryParams: HttpParams, tags: string[]) => {
    if (!tags?.length) {
      return queryParams;
    }

    tags.forEach((tag, index) => {
      queryParams = queryParams.append(`tags[${index}]`, tag);
    });

    return queryParams;
  };

  getProjects() {
    return new Promise<ProjectDto[]>((res, rej) => {
      this.rest.get(REST_PROJECTS + '?fiscal_view=1').then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getSimpleProjects(filter?: IProjectFilters): Observable<ISimpleProject[]> {
    const computedFilters = {};

    if (filter?.status_filter) {
      computedFilters['status_filter[]'] = filter.status_filter;
    }

    return this.rest.getWithObservable(PROJECTS_SIMPLE, {}, computedFilters).pipe(
      map((projects: ISimpleProject[]) => {
        if (!projects || !Array.isArray(projects)) {
          return [];
        }
        return projects.toSorted((a, b) => {
          const aField = a.title.replace(specialCharReplaceRegexp, '');
          const bField = b.title.replace(specialCharReplaceRegexp, '');
          return aField.localeCompare(bField);
        });
      }),
    );
  }

  getProject(id: number) {
    return new Promise<ProjectDto>((res, rej) => {
      this.rest.get(REST_PROJECTS + '/' + id + '?fiscal_view=1').then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getProjectById$(id: number) {
    return this.rest.getWithObservable(REST_PROJECTS + '/' + id + '?fiscal_view=1');
  }

  getProjectWithObservable(id: number): Observable<ProjectDto> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${id}`);
  }

  getBudgetTemplatesManager(
    propertyId: number,
    withTrashed = false,
  ): Observable<IBudgetTemplate<IBudgetTemplateItemManager>[]> {
    return this.rest.getWithObservable(BUDGET_TEMPLATES_MANAGER, {}, { propertyId, withTrashed });
  }

  getBudgetTemplatesGC(
    propertyId: number,
    withTrashed = false,
  ): Observable<IBudgetTemplate<IBudgetTemplateItemGC>[]> {
    return this.rest.getWithObservable(BUDGET_TEMPLATES_GC, {}, { propertyId, withTrashed });
  }

  createProject$(project: Partial<ProjectDto>) {
    return this.rest.postWithObservable(REST_PROJECTS, project);
  }

  updateProject(project: Partial<ProjectDto>): Promise<ProjectDto> {
    const body = project;

    return new Promise<ProjectDto>((res, rej) => {
      this.rest.put(REST_PROJECTS + '/' + project.id, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  updateProject$(project: Partial<ProjectDto>) {
    return this.rest.putWithObservable(`${REST_PROJECTS}/${project.id}`, project);
  }

  updateProjectStatus(id: number, status: number) {
    const body = {
      status,
    };

    return new Promise<any>((res, rej) => {
      this.rest.put(REST_PROJECT_STATUS + '/' + id, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  deleteProject$(id: number) {
    return this.getPossibleStatuses$().pipe(
      switchMap((statuses) => {
        const deletedStatus = statuses.find((status) => status.key === PROJECT_STATUS_KEY.DELETED);
        if (!deletedStatus.id) {
          throw new Error('Cannot delete project: deleted status not found');
        }
        return this.rest.putWithObservable(REST_PROJECT_STATUS + '/' + id, {
          status: deletedStatus.id,
        });
      }),
    );
  }

  getPossibleStatuses$(): Observable<ProjectStatusDto[]> {
    return this.rest.getWithObservable(REST_PROJECT_STATUS_TYPES);
  }

  getStats() {
    return new Promise<any>((res, rej) => {
      this.rest.get(REST_PROJECT_STATS).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  createProperty(property): Promise<any> {
    const body = property;

    return new Promise<any>((res, rej) => {
      this.rest.post(REST_PROPERTY, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getProperties(): Promise<IProperty[]> {
    return new Promise<any>((res, rej) => {
      this.rest.get(REST_PROPERTY).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  updateProperty(id, body): Promise<any> {
    return new Promise<any>((res, rej) => {
      this.rest.put(REST_PROPERTY + '/' + id, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  deleteProperty$(id) {
    return this.rest.delWithObservable(REST_PROPERTY + '/' + id).pipe(catchError(this.handleError));
  }

  getPropertyTypes(): Promise<any> {
    return new Promise<any>((res, rej) => {
      this.rest.get(REST_PROPERTY_TYPES).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getInvitedContractors(projectId: number): Observable<InvitedSubcontractor[]> {
    return this.rest.getWithObservable(
      `${REST_GET_INVITED_SUBCONTRACTORS_FOR_PROJECT}/${projectId}/sent`,
      {},
    );
  }

  createBid(projectId, value) {
    value.project_id = projectId;
    const body = value;

    return new Promise<any>((res, rej) => {
      this.rest.post(REST_PROJECT_BID, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  updateBid(bidId: any, values: any) {
    const body = values;

    return new Promise<any>((res, rej) => {
      this.rest.put(REST_PROJECT_BID + '/' + bidId, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  updateBidStatus(bidID, status) {
    const body = {
      status,
    };

    return new Promise<any>((res, rej) => {
      this.rest.put(REST_PROJECT_BID + '/status/' + bidID, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getBidByProject(projectID): Promise<ProjectBid[]> {
    return new Promise<any>((res, rej) => {
      this.rest.get(REST_PROJECT_BID + '?project_id=' + projectID).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  getNextProjectId(params: {
    property_id?: number;
    year?: number;
  }): Observable<{ next_custom_id: string }> {
    if (!params.property_id) {
      delete params.property_id;
    }
    if (!params.year) {
      delete params.year;
    }

    return this.rest.getWithObservable(REST_PROJECTS + '/next-custom-id', {}, params);
  }

  getProjectLineItemsSimple(
    projectId: number,
    onlySublines = false,
  ): Observable<ILineItemSimple[]> {
    return this.rest.getWithObservable(
      `${REST_PROJECTS}/${projectId}/${REST_LINE_ITEMS_SIMPLE}`,
      {},
      { onlySublines: onlySublines ? '1' : '0' },
    );
  }

  getAnticipatedIncomeItems(projectId: number): Observable<AnticipatedIncome[]> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/anticipated-incomes`);
  }

  // returns all forecast modifications for all budget lines of a project
  getForecastModificationsPerBudgetLine(
    projectId: number,
  ): Observable<IForecastModificationBudgetLine[]> {
    return this.rest.getWithObservable(
      `${FORECAST_MODIFICATIONS}/projects/${projectId}?onlySublines=1`,
    );
  }

  saveAnticipatedIncomeItems(projectId: number, data: any) {
    return this.rest.putWithObservable(`${REST_PROJECTS}/${projectId}/anticipated-incomes`, data);
  }
}
